import React, { useState } from 'react';
import { BarChart3, ArrowUpDown, ArrowUp, ArrowDown, Target, Clock, DollarSign } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import ProjectDetailModal from './ProjectDetailModal';

const ProjectPerformance = () => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedProject, setSelectedProject] = useState(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const { dashboardData } = useDashboard();

  if (!dashboardData) {
    return <div className="loading">Loading project performance data...</div>;
  }

  const { projects } = dashboardData.details;

  // Debug: Log the first project to see available fields
  if (projects && projects.length > 0) {
    console.log('First project data structure:', projects[0]);
  }

  // Handle case where projects might be undefined or empty
  if (!projects || !Array.isArray(projects) || projects.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Project Performance</h2>
        </div>
        <div className="empty-state">
          <BarChart3 size={48} color="#9ca3af" />
          <p>No project data available</p>
          <p className="text-sm text-gray-500">Project performance data will appear here when available</p>
        </div>
      </div>
    );
  }

  // Transform project data to match our table structure
  const transformedProjects = projects.map(project => ({
    id: project.projectId,
    projectName: project.projectName,
    progressPercentage: project.progressPercentage || project.progress || 0,
    allocatedHours: project.allocatedHours || 0,
    trackedHours: project.trackedHours || 0,
    billableHours: project.billableHours || 0,
    status: project.status?.label || project.status?.value || project.status || 'unknown',
    owner: project.owner ? `${project.owner.firstName} ${project.owner.lastName}`.trim() : 'Unknown',
    startDate: project.startDate,
    endDate: 0,
    // Keep the original project data for the modal
    originalProject: project
  }));

  // Apply status and owner filtering
  const filteredProjects = transformedProjects.filter(project => {
    const statusMatch = statusFilter === 'all' || project.status.toLowerCase() === statusFilter.toLowerCase();
    const ownerMatch = ownerFilter === 'all' || project.owner.toLowerCase() === ownerFilter.toLowerCase();
    return statusMatch && ownerMatch;
  });

  // Get unique statuses and owners for filter dropdowns
  const uniqueStatuses = ['all', ...Array.from(new Set(transformedProjects.map(p => p.status)))];
  const uniqueOwners = ['all', ...Array.from(new Set(transformedProjects.map(p => p.owner)))].sort();

  // Sorting function
  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];
      
      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Handle sort click
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={14} />;
    }
    return sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  // Apply sorting if configured
  let sortedProjects = filteredProjects; // Use filteredProjects here
  if (sortConfig.key) {
    sortedProjects = sortData(filteredProjects, sortConfig.key, sortConfig.direction);
  }

  // Calculate summary metrics
  const getSummaryMetrics = () => {
    const totalProjects = sortedProjects.length;
    const averageProgress = totalProjects > 0 
      ? sortedProjects.reduce((sum, project) => sum + project.progressPercentage, 0) / totalProjects 
      : 0;
    const totalAllocatedHours = sortedProjects.reduce((sum, project) => sum + project.allocatedHours, 0);
    const totalTrackedHours = sortedProjects.reduce((sum, project) => sum + project.trackedHours, 0);
    const totalBillableHours = sortedProjects.reduce((sum, project) => sum + project.billableHours, 0);
    
    return {
      totalProjects,
      averageProgress,
      totalAllocatedHours,
      totalTrackedHours,
      totalBillableHours
    };
  };

  const summaryMetrics = getSummaryMetrics();

  // Handle project click
  const handleProjectClick = (project) => {
    setSelectedProject(project.originalProject);
    setIsProjectModalOpen(true);
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Project Performance</h2>
        <div className="card-actions">
          <button className="btn-secondary">Export Data</button>
        </div>
      </div>

      <div className="utilization-summary">
        <div className="summary-item">
          <span className="summary-label">Total Projects</span>
          <span className="summary-value">{summaryMetrics.totalProjects}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Average Progress</span>
          <span className="summary-value">{summaryMetrics.averageProgress.toFixed(1)}%</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Allocated</span>
          <span className="summary-value">{summaryMetrics.totalAllocatedHours.toFixed(1)}h</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Tracked</span>
          <span className="summary-value">{summaryMetrics.totalTrackedHours.toFixed(1)}h</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Billable</span>
          <span className="summary-value">{summaryMetrics.totalBillableHours.toFixed(1)}h</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Efficiency</span>
          <span className="summary-value">
            {summaryMetrics.totalAllocatedHours > 0 
              ? ((summaryMetrics.totalTrackedHours / summaryMetrics.totalAllocatedHours) * 100).toFixed(1)
              : 0}%
          </span>
        </div>
      </div>

      <div className="utilization-table">
        <h4>Project Performance Details</h4>
        
        {/* Filters */}
        <div className="filter-section">
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="status-filter" className="filter-label">Filter by Status:</label>
              <select
                id="status-filter"
                className="filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>
                    {status === 'all' ? 'All Statuses' : status}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="owner-filter" className="filter-label">Filter by Owner:</label>
              <select
                id="owner-filter"
                className="filter-select"
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
              >
                {uniqueOwners.map(owner => (
                  <option key={owner} value={owner}>
                    {owner === 'all' ? 'All Owners' : owner}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Filter Summary */}
          {(statusFilter !== 'all' || ownerFilter !== 'all') && (
            <div className="filter-summary">
              <span className="summary-label">Active filters:</span>
              <div className="filter-tags">
                {statusFilter !== 'all' && (
                  <span className="filter-tag">
                    Status: {statusFilter}
                    <button 
                      className="filter-tag-remove" 
                      onClick={() => setStatusFilter('all')}
                    >
                      ×
                    </button>
                  </span>
                )}
                {ownerFilter !== 'all' && (
                  <span className="filter-tag">
                    Owner: {ownerFilter}
                    <button 
                      className="filter-tag-remove" 
                      onClick={() => setOwnerFilter('all')}
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('projectName')} className="sortable-header">
                  Project Name
                  {getSortIcon('projectName')}
                </th>
                <th onClick={() => handleSort('progressPercentage')} className="sortable-header">
                  <Target size={14} />
                  Progress
                  {getSortIcon('progressPercentage')}
                </th>
                <th onClick={() => handleSort('allocatedHours')} className="sortable-header">
                  <Clock size={14} />
                  Allocated Hours
                  {getSortIcon('allocatedHours')}
                </th>
                <th onClick={() => handleSort('trackedHours')} className="sortable-header">
                  <Clock size={14} />
                  Tracked Hours
                  {getSortIcon('trackedHours')}
                </th>
                                 <th onClick={() => handleSort('billableHours')} className="sortable-header">
                   <DollarSign size={14} />
                   Billable Hours
                   {getSortIcon('billableHours')}
                 </th>
                 <th onClick={() => handleSort('status')} className="sortable-header">
                   Status
                   {getSortIcon('status')}
                 </th>
              </tr>
            </thead>
            <tbody>
                             {sortedProjects.map((project) => (
                 <tr key={project.id} className="clickable-row" onClick={() => handleProjectClick(project)}>
                  <td>
                    <div className="project-name-cell">
                      <strong>{project.projectName}</strong>
                    </div>
                  </td>
                  <td>
                    <div className="progress-cell">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${project.progressPercentage}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">{project.progressPercentage}%</span>
                    </div>
                  </td>
                                     <td>{project.allocatedHours.toFixed(1)}h</td>
                   <td>{project.trackedHours.toFixed(1)}h</td>
                   <td>{project.billableHours.toFixed(1)}h</td>
                   <td>
                     <span className={`status-badge ${project.status.toLowerCase().replace(/\s+/g, '-')}`}>
                       {project.status}
                     </span>
                   </td>
                </tr>
              ))}
            </tbody>
          </table>
                 </div>
       </div>

       <ProjectDetailModal 
         project={selectedProject} 
         isOpen={isProjectModalOpen} 
         onClose={() => setIsProjectModalOpen(false)}
       />
     </div>
   );
 };

export default ProjectPerformance;
